import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, fetchCapabilities, fetchMe, OfflineError, signOut } from './lib/api';
import { exportOutputs, type OutputSpec } from './lib/exporter';
import { startUsageFlushing, track } from './lib/usage';
import type { PdfMetadata } from './lib/pdfEngine';
import type { StampOptions } from './lib/stamps';
import { ingestFiles } from './lib/ingest';
import * as store from './lib/documentStore';
import { useOnline } from './lib/useOnline';
import { useCanvasHistory } from './lib/useCanvasHistory';
import { cachedSession, forgetSession, graceRemainingDays, rememberSession } from './lib/sessionCache';
import { forgetAll, forgetDocument } from './lib/thumbnails';
import type { Capabilities, PageItem, SessionInfo, StoredDocument } from './lib/types';
import { AccountMenu } from './components/AccountMenu';
import { UsageDialog } from './components/UsageDialog';
import { LoginScreen } from './components/LoginScreen';
import { HelpDialog } from './components/HelpDialog';
import { DropZone } from './components/DropZone';
import { ExportDialog } from './components/ExportDialog';
import { ExportOptionsDialog } from './components/ExportOptionsDialog';
import { PageGrid } from './components/PageGrid';
import { PagePreview } from './components/PagePreview';
import { Toolbar } from './components/Toolbar';
import { WordTip } from './components/WordTip';
import { XIcon } from './components/Icons';

/**
 * What the app can do with no server reachable.
 *
 * Not a placeholder: offline these genuinely are the only formats that work,
 * because everything else needs LibreOffice.
 */
const OFFLINE_CAPABILITIES: Capabilities = {
  officeConversion: false,
  maxUploadMb: 100,
  maxFilesPerUpload: 25,
  acceptedExtensions: ['pdf', 'jpg', 'jpeg', 'png'],
};

let uidCounter = 0;
/** The uid `delta` places from `current` in the canvas, wrapping at both ends. */
function stepPreview(pages: PageItem[], current: string | null, delta: number): string | null {
  if (!current || pages.length === 0) return current;
  const index = pages.findIndex((page) => page.uid === current);
  if (index === -1) return current;
  return pages[(index + delta + pages.length) % pages.length]!.uid;
}

function nextUid(): string {
  uidCounter += 1;
  return `p${uidCounter}`;
}

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png']);

/**
 * Counts what was added, by the kind of file it came from.
 *
 * Only the extension is looked at, and only to pick which counter to add to —
 * no name reaches the recorder.
 */
function countImports(files: File[], failed: Array<{ name: string }>): void {
  const refused = new Set(failed.map((f) => f.name));
  let pdfs = 0;
  let images = 0;
  let office = 0;
  for (const file of files) {
    if (refused.has(file.name)) continue;
    const extension = /\.([^.]+)$/.exec(file.name)?.[1]?.toLowerCase() ?? '';
    if (extension === 'pdf') pdfs += 1;
    else if (IMAGE_EXTENSIONS.has(extension)) images += 1;
    else office += 1;
  }
  if (pdfs > 0) track('import_pdf', pdfs);
  if (images > 0) track('import_image', images);
  if (office > 0) {
    track('import_office', office);
    // The one operation that actually touches the server, worth its own count.
    track('convert_office', office);
  }
}

export default function App() {
  const [session, setSession] = useState<SessionInfo | null>(null);
  /** Set only while running on a cached identity, i.e. offline. */
  const [lastVerified, setLastVerified] = useState<number | null>(null);
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);

  const [documents, setDocuments] = useState<StoredDocument[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  /*
   * The arrangement, with undo. Selection is deliberately outside it: undoing
   * should put pages back, not silently change what is highlighted.
   */
  const canvas = useCanvasHistory({ pages: [], splitAfter: new Set<string>() });
  const { pages, splitAfter } = canvas;

  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  /*
   * Stamps live here rather than in the export dialog: they are a property of
   * the document being built, adjusted with a preview, and only applied at the
   * moment of export.
   */
  const [stamps, setStamps] = useState<StampOptions>({});
  /** Which page the preview modal is showing, by uid. */
  const [previewUid, setPreviewUid] = useState<string | null>(null);
  const [windowDragging, setWindowDragging] = useState(false);

  const [showUsage, setShowUsage] = useState(false);
  /** Whether to offer the Usage item at all. Only an admin ever sees it. */
  const [usageAdmin, setUsageAdmin] = useState(false);

  // Sends anything queued from an earlier, offline session. Above the early
  // returns below, or it would run on some renders and not others.
  useEffect(() => startUsageFlushing(), []);

  useEffect(() => {
    // A plain failure here means no Usage item, which is the right default.
    void fetch('/api/usage/access', { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => setUsageAdmin(Boolean(body?.admin)))
      .catch(() => setUsageAdmin(false));
  }, []);

  const openFilePicker = useRef<() => void>(() => undefined);
  const registerOpen = useCallback((open: () => void) => {
    openFilePicker.current = open;
  }, []);

  const lastClickedUid = useRef<string | null>(null);

  /** Whether our own server answers — not merely whether there is a network. */
  const online = useOnline();

  // ----- bootstrap -----

  useEffect(() => {
    void (async () => {
      // Local documents first: they are on this device and do not need the
      // network, so the canvas fills even if the server is unreachable.
      try {
        const existing = await store.hydrate();
        if (existing.length > 0) {
          setDocuments(existing);
          canvas.replace((c) => ({
            ...c,
            pages: existing.flatMap((doc) =>
              Array.from({ length: doc.pageCount }, (_unused, index) => ({
                uid: nextUid(),
                docId: doc.id,
                docName: doc.name,
                pageIndex: index,
                rotation: 0,
              })),
            ),
          }));
        }
      } catch {
        // Blocked storage — start empty rather than refusing to load.
      }

      try {
        const me = await fetchMe();
        setSession(me);
        rememberSession(me);
        setCapabilities(await fetchCapabilities());
      } catch (err) {
        if (err instanceof OfflineError) {
          // Offline: fall back to the identity from the last verified sign-in,
          // within its grace window. Everything local still works.
          const cached = cachedSession();
          if (cached) {
            setSession(cached.session);
            setLastVerified(cached.verifiedAt);
            // Must be set too: the app waits for both before it renders, so
            // leaving this unset strands an offline launch on "Loading…".
            setCapabilities(OFFLINE_CAPABILITIES);
          } else {
            setNeedsSignIn(true);
          }
        } else if (err instanceof ApiError && err.status === 401) {
          forgetSession();
          setNeedsSignIn(true);
        } else {
          setBootError(err instanceof Error ? err.message : 'The app could not start.');
        }
      }
    })();
  }, []);

  const handleFiles = useCallback(
    async (files: File[]) => {
      setError(null);
      setWarnings([]);
      setBusy(true);
      try {
        const result = await ingestFiles(files, online);
        if (result.documents.length > 0) {
          setDocuments((current) => [...current, ...result.documents]);
          canvas.update((c) => ({
            ...c,
            pages: [
              ...c.pages,
              ...result.documents.flatMap((doc) =>
                Array.from({ length: doc.pageCount }, (_unused, index) => ({
                  uid: nextUid(),
                  docId: doc.id,
                  docName: doc.name,
                  pageIndex: index,
                  rotation: 0,
                })),
              ),
            ],
          }));
        }
        if (result.failed.length > 0) {
          setWarnings(result.failed.map((f) => `${f.name}: ${f.reason}`));
        }
        countImports(files, result.failed);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Those files could not be added.');
      } finally {
        setBusy(false);
      }
    },
    [online],
  );

  // A drag anywhere over the window shows a full-page drop target.
  useEffect(() => {
    let depth = 0;
    function onEnter(event: DragEvent) {
      if (!event.dataTransfer?.types.includes('Files')) return;
      depth += 1;
      setWindowDragging(true);
    }
    function onLeave() {
      depth = Math.max(0, depth - 1);
      if (depth === 0) setWindowDragging(false);
    }
    function onDrop(event: DragEvent) {
      depth = 0;
      setWindowDragging(false);
      const files = [...(event.dataTransfer?.files ?? [])];
      if (files.length > 0) {
        event.preventDefault();
        void handleFiles(files);
      }
    }
    function onOver(event: DragEvent) {
      if (event.dataTransfer?.types.includes('Files')) event.preventDefault();
    }
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('dragover', onOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [handleFiles]);

  // The server gets the final say the moment it can be reached again: an
  // account disabled while someone was offline loses access here, which is what
  // keeps the offline grace period from becoming a way around sign-in.
  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    void (async () => {
      try {
        const me = await fetchMe();
        if (cancelled) return;
        setSession(me);
        rememberSession(me);
        setLastVerified(null);
        setCapabilities(await fetchCapabilities());
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          forgetSession();
          setNeedsSignIn(true);
        }
        // An OfflineError here just means the probe and this raced; the next
        // interval will settle it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [online]);

  // ----- selection -----

  const toggleSelect = useCallback(
    (uid: string, additive: boolean, range: boolean) => {
      setSelected((current) => {
        const next = new Set(current);
        if (range && lastClickedUid.current) {
          const from = pages.findIndex((page) => page.uid === lastClickedUid.current);
          const to = pages.findIndex((page) => page.uid === uid);
          if (from !== -1 && to !== -1) {
            for (let i = Math.min(from, to); i <= Math.max(from, to); i += 1) {
              next.add(pages[i]!.uid);
            }
            return next;
          }
        }
        if (!additive && !range) {
          next.has(uid) ? next.delete(uid) : next.add(uid);
        } else if (next.has(uid)) {
          next.delete(uid);
        } else {
          next.add(uid);
        }
        return next;
      });
      lastClickedUid.current = uid;
    },
    [pages],
  );

  const selectAll = useCallback(() => setSelected(new Set(pages.map((p) => p.uid))), [pages]);
  const selectNone = useCallback(() => setSelected(new Set()), []);

  // ----- page operations -----

  const rotatePages = useCallback(
    (uids: Set<string>, delta: number) => {
      track('rotate_page', uids.size);
      canvas.update((c) => ({
        ...c,
        pages: c.pages.map((page) =>
          uids.has(page.uid)
            ? { ...page, rotation: (((page.rotation + delta) % 360) + 360) % 360 }
            : page,
        ),
      }));
    },
    [canvas],
  );

  const removePages = useCallback(
    (uids: Set<string>) => {
      track('delete_page', uids.size);
      canvas.update((c) => {
        const splits = new Set(c.splitAfter);
        for (const uid of uids) splits.delete(uid);
        return { pages: c.pages.filter((page) => !uids.has(page.uid)), splitAfter: splits };
      });
      setPreviewUid((current) => (current && uids.has(current) ? null : current));
      setSelected((current) => {
        const next = new Set(current);
        for (const uid of uids) next.delete(uid);
        return next;
      });
    },
    [canvas],
  );

  /**
   * Inserts a blank page after the last selected page, or at the end when
   * nothing is selected — the two places someone means by "here".
   */
  const insertBlank = useCallback(() => {
    track('add_blank_page');
    canvas.update((c) => {
      const blank: PageItem = {
        uid: nextUid(),
        docId: '',
        docName: 'Blank page',
        pageIndex: 0,
        rotation: 0,
        blank: true,
      };
      let at = c.pages.length;
      for (let i = c.pages.length - 1; i >= 0; i -= 1) {
        if (selected.has(c.pages[i]!.uid)) {
          at = i + 1;
          break;
        }
      }
      return { ...c, pages: [...c.pages.slice(0, at), blank, ...c.pages.slice(at)] };
    });
  }, [canvas, selected]);

  /** Replaces the text added to one page. Undoable like any other edit. */
  const setPageTexts = useCallback(
    (uid: string, texts: NonNullable<PageItem['texts']>) => {
      canvas.update((c) => ({
        ...c,
        pages: c.pages.map((page) =>
          page.uid === uid ? { ...page, texts: texts.length > 0 ? texts : undefined } : page,
        ),
      }));
    },
    [canvas],
  );

  const toggleSplit = useCallback(
    (uid: string) => {
      if (!canvas.splitAfter.has(uid)) track('split');
      canvas.update((c) => {
        const next = new Set(c.splitAfter);
        next.has(uid) ? next.delete(uid) : next.add(uid);
        return { ...c, splitAfter: next };
      });
    },
    [canvas],
  );

  /**
   * Moves the dragged page to the drop target. When the dragged page is part of
   * a multi-page selection, the whole selection travels with it as one block.
   */
  const reorder = useCallback(
    (activeUid: string, overUid: string) => {
      track('reorder_page', selected.has(activeUid) && selected.size > 1 ? selected.size : 1);
      canvas.update((c) => {
        const current = c.pages;
        const movingSet =
          selected.has(activeUid) && selected.size > 1 ? selected : new Set([activeUid]);

        const overIndex = current.findIndex((page) => page.uid === overUid);
        // Returning the state unchanged means no undo step is recorded.
        if (overIndex === -1) return c;

        const moving = current.filter((page) => movingSet.has(page.uid));
        const rest = current.filter((page) => !movingSet.has(page.uid));

        // Insert before the drop target, counted among the pages that stay put.
        let insertAt = 0;
        for (let i = 0; i < overIndex; i += 1) {
          if (!movingSet.has(current[i]!.uid)) insertAt += 1;
        }
        // Dragging downwards should land after the target, matching the preview.
        const activeIndex = current.findIndex((page) => page.uid === activeUid);
        if (activeIndex !== -1 && activeIndex < overIndex) insertAt += 1;

        return { ...c, pages: [...rest.slice(0, insertAt), ...moving, ...rest.slice(insertAt)] };
      });
    },
    [canvas, selected],
  );

  const splitByDocument = useCallback(() => {
    const next = new Set<string>();
    for (let i = 0; i < pages.length - 1; i += 1) {
      if (pages[i]!.docId !== pages[i + 1]!.docId) next.add(pages[i]!.uid);
    }
    canvas.update((c) => ({ ...c, splitAfter: next }));
  }, [canvas, pages]);

  const splitAfterSelected = useCallback(() => {
    canvas.update((c) => {
      const next = new Set(c.splitAfter);
      for (const uid of selected) next.add(uid);
      return { ...c, splitAfter: next };
    });
  }, [canvas, selected]);

  // ----- uploads -----

  const removeDocument = useCallback(async (docId: string) => {
    await store.remove(docId);
    forgetDocument(docId);
    setDocuments((current) => current.filter((doc) => doc.id !== docId));
    // Its bytes are gone from IndexedDB, so an undo could not honestly bring
    // those pages back. The history starts again from here.
    canvas.reset({
      pages: pages.filter((page) => page.docId !== docId),
      splitAfter,
    });
  }, [canvas, pages, splitAfter]);

  const startOver = useCallback(async () => {
    await store.clear();
    forgetAll();
    setDocuments([]);
    canvas.reset({ pages: [], splitAfter: new Set() });
    setSelected(new Set());
    setWarnings([]);
  }, []);

  // ----- outputs -----

  const outputs = useMemo<PageItem[][]>(() => {
    const groups: PageItem[][] = [];
    let currentGroup: PageItem[] = [];
    pages.forEach((page, index) => {
      currentGroup.push(page);
      if (splitAfter.has(page.uid) && index < pages.length - 1) {
        groups.push(currentGroup);
        currentGroup = [];
      }
    });
    if (currentGroup.length > 0) groups.push(currentGroup);
    return groups;
  }, [pages, splitAfter]);

  const defaultExportName = useMemo(() => {
    if (documents.length === 1) return documents[0]!.name.replace(/\.pdf$/i, '');
    return outputs.length > 1 ? 'split' : 'combined';
  }, [documents, outputs.length]);

  const runExport = useCallback(
    async (baseName: string, metadata: PdfMetadata) => {
      setExporting(true);
      setError(null);
      try {
        const specs: OutputSpec[] = outputs.map((group, index) => ({
          name: outputs.length === 1 ? baseName : `${baseName} ${index + 1}`,
          pages: group,
        }));
        await exportOutputs(specs, baseName, metadata, stamps);
        track('export_pdf', specs.reduce((total, spec) => total + spec.pages.length, 0));
        if (stamps.pageNumbers || stamps.watermark) track('stamp');
        setShowExport(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'The export failed.');
      } finally {
        setExporting(false);
      }
    },
    [outputs, stamps],
  );

  /*
   * How many of each file's pages are still on the canvas. A file whose pages
   * have all been deleted stays listed — its bytes are what an undo would need,
   * and removing it would make undo restore pages with no document behind them.
   * It says it is unused instead, and the × removes it for real.
   */
  const pagesInUse = useMemo(() => {
    const counts = new Map<string, number>();
    for (const page of pages) {
      if (!page.blank) counts.set(page.docId, (counts.get(page.docId) ?? 0) + 1);
    }
    return counts;
  }, [pages]);

  // ----- keyboard shortcuts -----

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (showExport || showHelp || showOptions || showUsage || previewUid) return;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        // Shift+Z redoes, matching every other app on both platforms.
        if (event.shiftKey) canvas.redo();
        else canvas.undo();
      } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'y') {
        // Windows habit.
        event.preventDefault();
        canvas.redo();
      } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        selectAll();
      } else if ((event.key === 'Delete' || event.key === 'Backspace') && selected.size > 0) {
        event.preventDefault();
        removePages(selected);
      } else if (event.key === 'Escape') {
        selectNone();
      } else if (selected.size > 0 && (event.key === '[' || event.key === ']')) {
        event.preventDefault();
        rotatePages(selected, event.key === '[' ? -90 : 90);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectAll, selectNone, selected, removePages, rotatePages, showExport, showHelp, showOptions, showUsage, previewUid, canvas]);

  // ----- render -----

  if (needsSignIn) return <LoginScreen />;

  if (bootError) {
    return (
      <div className="centered-message">
        <div className="card">
          <h1>Unable to start</h1>
          <p>{bootError}</p>
          <button className="btn" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }

  if (!session || !capabilities) {
    return (
      <div className="centered-message">
        <div className="card">
          <p style={{ margin: 0 }}>Loading…</p>
        </div>
      </div>
    );
  }

  const totalPages = pages.length;

  const previewIndex = previewUid ? pages.findIndex((page) => page.uid === previewUid) : -1;

  return (
    <div className="app">
      <header className="header">
        <div className="header__brand">
          <img src="/on-services-mark.svg" alt="" width={18} height={22} />
          PDF Toolkit <span>ON Services</span>
        </div>
        <div className="header__spacer" />
        {documents.length > 0 && (
          <button className="btn btn--sm btn--ghost" onClick={() => void startOver()}>
            Start over
          </button>
        )}
        <AccountMenu
          user={session.user}
          hasPhoto={session.hasPhoto}
          authDisabled={session.authDisabled}
          onSignOut={() => void signOut()}
          onHelp={() => setShowHelp(true)}
          onUsage={usageAdmin ? () => setShowUsage(true) : undefined}
        />
      </header>

      {lastVerified !== null && (
        <div className="banner banner--warn">
          <div>
            <strong>Working offline.</strong> Page edits and exports run on this device and are
            unaffected. Office conversion needs the server. Sign-in was last confirmed{' '}
            {new Date(lastVerified).toLocaleDateString()} — reconnect within{' '}
            {graceRemainingDays(lastVerified)} day
            {graceRemainingDays(lastVerified) === 1 ? '' : 's'} to keep access.
          </div>
        </div>
      )}

      {error && (
        <div className="banner banner--error">
          <span>{error}</span>
          <button className="icon-btn banner__close" onClick={() => setError(null)} aria-label="Dismiss">
            <XIcon />
          </button>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="banner banner--warn">
          <div>
            <strong>Some files were skipped:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
          <button className="icon-btn banner__close" onClick={() => setWarnings([])} aria-label="Dismiss">
            <XIcon />
          </button>
        </div>
      )}

      {busy && (
        <div className="progress">
          <div className="progress__bar progress__bar--indeterminate" />
        </div>
      )}

      <Toolbar
        pageCount={totalPages}
        selectedCount={selected.size}
        outputCount={outputs.length}
        busy={busy}
        onAddFiles={() => openFilePicker.current()}
        canUndo={canvas.canUndo}
        canRedo={canvas.canRedo}
        onUndo={canvas.undo}
        onRedo={canvas.redo}
        onSelectAll={selectAll}
        onSelectNone={selectNone}
        onRotateSelected={(delta) => rotatePages(selected, delta)}
        onDeleteSelected={() => removePages(selected)}
        onInsertBlank={insertBlank}
        onSplitSelected={splitAfterSelected}
        onSplitByDocument={splitByDocument}
        onClearSplits={() => canvas.update((c) => ({ ...c, splitAfter: new Set() }))}
        stampsActive={Boolean(stamps.pageNumbers || stamps.watermark)}
        onExportOptions={() => setShowOptions(true)}
        onExport={() => setShowExport(true)}
      />

      <div className="main">
        <aside className="sidebar">
          <h2 className="section-title">Source files</h2>
          {documents.length === 0 ? (
            <p className="empty-note">No files yet.</p>
          ) : (
            <ul className="doc-list">
              {documents.map((doc) => {
                const inUse = pagesInUse.get(doc.id) ?? 0;
                return (
                <li className={inUse === 0 ? 'doc-item doc-item--unused' : 'doc-item'} key={doc.id}>
                  <div className="doc-item__body">
                    <div className="doc-item__name" title={doc.name}>
                      {doc.name}
                    </div>
                    <div className="doc-item__meta">
                      {inUse === doc.pageCount
                        ? `${doc.pageCount} page${doc.pageCount === 1 ? '' : 's'}`
                        : `${inUse} of ${doc.pageCount} pages`}{' '}
                      · {formatBytes(doc.byteSize)}
                      {doc.converted && (
                        <>
                          {' '}
                          <span className="badge">{doc.sourceFormat}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    className="icon-btn icon-btn--danger"
                    onClick={() => void removeDocument(doc.id)}
                    title="Remove this file and all of its pages"
                    aria-label={`Remove ${doc.name}`}
                  >
                    <XIcon />
                  </button>
                </li>
                );
              })}
            </ul>
          )}

          <DropZone
            accepted={capabilities.acceptedExtensions}
            maxUploadMb={capabilities.maxUploadMb}
            officeConversion={capabilities.officeConversion}
            disabled={busy}
            onFiles={(files) => void handleFiles(files)}
            registerOpen={registerOpen}
          />

          {!capabilities.officeConversion && (
            <p className="empty-note" style={{ marginTop: 12 }}>
              {online
                ? 'Office conversion is unavailable — LibreOffice is not installed on this server.'
                : 'Office conversion needs the server, which is unreachable offline. PDFs and images still work.'}
            </p>
          )}

          <WordTip />
        </aside>

        <section className="workspace">
          {totalPages === 0 ? (
            <div className="empty-state">
              <div>
                <h2>Add files to begin</h2>
                <p>
                  Drop PDFs{capabilities.officeConversion ? ', Word, Excel, PowerPoint' : ''} or
                  images anywhere on this page. Every page appears here, where you can reorder it by
                  dragging, rotate it, delete it, or split the set into several files.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid-scroll">
              <PageGrid
                pages={pages}
                selected={selected}
                splitAfter={splitAfter}
                onReorder={reorder}
                onToggleSelect={toggleSelect}
                onRotate={(uid, delta) => rotatePages(new Set([uid]), delta)}
                onRemove={(uid) => removePages(new Set([uid]))}
                onToggleSplit={toggleSplit}
                onPreview={setPreviewUid}
              />
            </div>
          )}
        </section>
      </div>

      {previewIndex !== -1 && (
        <PagePreview
          page={pages[previewIndex]!}
          position={previewIndex + 1}
          totalPages={pages.length}
          onClose={() => setPreviewUid(null)}
          /*
           * Stepped from the current uid rather than the index captured at
           * render: pressing an arrow key several times quickly would otherwise
           * have every handler compute from the same stale index and move a
           * single page in total.
           */
          onPrevious={() => setPreviewUid((current) => stepPreview(pages, current, -1))}
          onNext={() => setPreviewUid((current) => stepPreview(pages, current, 1))}
          onRotate={(uid, delta) => rotatePages(new Set([uid]), delta)}
          onTextsChange={setPageTexts}
        />
      )}

      {showHelp && <HelpDialog onClose={() => setShowHelp(false)} />}
      {showUsage && <UsageDialog onClose={() => setShowUsage(false)} />}

      {showOptions && pages.length > 0 && (
        <ExportOptionsDialog
          pages={pages}
          stamps={stamps}
          onChange={setStamps}
          onClose={() => setShowOptions(false)}
        />
      )}

      {showExport && (
        <ExportDialog
          outputCount={outputs.length}
          totalPages={totalPages}
          defaultName={defaultExportName}
          defaultAuthor={session.user.name}
          busy={exporting}
          onCancel={() => setShowExport(false)}
          onConfirm={(name, metadata) => void runExport(name, metadata)}
        />
      )}

      {windowDragging && <div className="overlay-drop">Drop files to add them</div>}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
